import {useRef, useCallback, useEffect, useMemo, type ChangeEvent, type PointerEvent as ReactPointerEvent} from 'react'
import {Box, TextField, Slider, Typography} from '@mui/material'
import * as THREE from 'three'
import {useAppSelector} from '../../../common/store'
import {selectActiveScene} from '../../../common/components/store/scenesSlice'
import {useViewportAspect} from '../../../common/components/viewportSizeStore'
import {CAMERA_FOV_DEG} from '../../../common/constants/scene3d'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Constants ─────────────────────────────────────────────────────

const PAD_SIZE = 120
const HANDLE_RADIUS = 6
// Hard upper safety clamp for typed input — beyond this the camera far plane
// (2000) starts clipping objects, and shadows/scene bounds break down.
const MAX_TYPED_HALF_RANGE = 2000
// Z range is tighter than X/Y and one-sided on purpose — 3D sprites & text
// only need a small forward offset to sit just in front of the device;
// negative Z would put them behind / inside it.
const Z_MIN = 0
const Z_MAX = 50
// PerspectiveCamera near/far. Match ViewportV2's runtime camera so the
// projection matrices align exactly.
const CAM_NEAR = 1
const CAM_FAR = 2000

// ── PositionPad3D — XY pad + vertical Z slider + text inputs ─────
//
// The XY pad maps screen-space (NDC) ↔ world XY on the Z=z plane through a
// temp PerspectiveCamera that mirrors the live viewport camera (position +
// the SAME orientation via `cameraRotation` + FOV + CANVAS aspect). The
// runtime camera uses `setViewOffset` to render the wider canvas around the
// export-aspect rect, so what the user actually sees on screen is bounded by
// canvas aspect — using export aspect here would tighten the horizontal range
// too much. Dragging the handle to a pad corner places the object at the
// corresponding visible corner of the canvas, regardless of camera zoom OR
// orbit angle.
//
// IMPORTANT: the temp camera copies the live camera's actual `cameraRotation`
// rather than assuming `lookAt(0,0,0)`. OrbitControls panning moves the orbit
// target off the world origin, so after a pan the real camera no longer points
// at (0,0,0). Assuming it did made the NDC↔world mapping correct only when the
// camera was (near) perpendicular/top-down — at an orbit angle the assumed view
// direction was rotated away from the true one and the reachable edge was off.
//
// When the export aspect is WIDER than the canvas aspect — the common case
// for carousel renders (N slices side-by-side) and narrow viewports — the
// runtime camera's `setViewOffset` enlarges the vertical frustum by
// `vpH/subRegion.h = exportAspect/canvasAspect` so the strip can contain-fit
// inside the canvas. We compensate by widening the temp camera's V_FOV by
// the same factor, keeping aspect = canvasAspect.

interface PositionPad3DProps {
  x: number
  y: number
  z: number
  onChangeX: (v: number) => void
  onChangeY: (v: number) => void
  onChangeZ: (v: number) => void
  /** Called on mouseup / slider committed / input blur to seal the undo merge chain */
  onDragEnd?: () => void
}

const PositionPad3D = ({x, y, z, onChangeX, onChangeY, onChangeZ, onDragEnd}: PositionPad3DProps) => {
  const tokens = useAppTokens()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const draggingRef = useRef(false)

  // Camera position + rotation + live viewport canvas aspect drive the
  // projection. We mirror the live camera's actual orientation (`cameraRotation`)
  // so the mapping stays correct after the user pans (which moves the orbit
  // target off the world origin). Canvas aspect (not export aspect) is what
  // matches what the user actually sees on screen — the runtime camera renders
  // the full canvas via setViewOffset and the export frame is just an overlay
  // rectangle inside.
  const cameraPosition = useAppSelector((s) => selectActiveScene(s).cameraPosition)
  const cameraRotation = useAppSelector((s) => selectActiveScene(s).cameraRotation)
  const exportWidth = useAppSelector((s) => selectActiveScene(s).exportWidth)
  const exportHeight = useAppSelector((s) => selectActiveScene(s).exportHeight)
  const carouselEnabled = useAppSelector((s) => selectActiveScene(s).exportCarouselEnabled)
  const carouselCount = useAppSelector((s) => selectActiveScene(s).exportCarouselCount)
  const canvasAspect = useViewportAspect()
  const camX = cameraPosition[0]
  const camY = cameraPosition[1]
  const camZ = cameraPosition[2]
  const rotX = cameraRotation[0]
  const rotY = cameraRotation[1]
  const rotZ = cameraRotation[2]

  // Effective vertical FOV. When the export frame's aspect is taller than the
  // canvas (`exportAspect <= canvasAspect`), the live camera's setViewOffset
  // is a horizontal expansion → vertical FOV unchanged. When it's wider
  // (typical for carousel strips), setViewOffset expands the vertical frustum
  // by `exportAspect / canvasAspect` so the strip contain-fits inside the
  // canvas. Mirror that here so the pad's NDC ↔ world projection matches what
  // the user actually sees in the editor.
  const slices = carouselEnabled ? carouselCount : 1
  const exportAspect = (exportWidth * slices) / exportHeight
  const effectiveFovDeg = useMemo(() => {
    if (exportAspect <= canvasAspect) return CAMERA_FOV_DEG
    const fovRad = (CAMERA_FOV_DEG * Math.PI) / 180
    const expandedRad = 2 * Math.atan(Math.tan(fovRad / 2) * (exportAspect / canvasAspect))
    return (expandedRad * 180) / Math.PI
  }, [exportAspect, canvasAspect])

  const tempCamera = useMemo(() => {
    const c = new THREE.PerspectiveCamera(effectiveFovDeg, canvasAspect, CAM_NEAR, CAM_FAR)
    c.position.set(camX, camY, camZ)
    // Match the live camera's exact orientation (default Euler order 'XYZ',
    // same as Three's PerspectiveCamera) instead of assuming it looks at the
    // origin — keeps the NDC↔world mapping correct under pan + orbit.
    c.rotation.set(rotX, rotY, rotZ)
    c.updateMatrixWorld()
    return c
  }, [camX, camY, camZ, rotX, rotY, rotZ, canvasAspect, effectiveFovDeg])

  // Pad pixel → world XY on plane Z = planeZ. Unproject NDC through the camera
  // to get a world-space ray, then intersect with the target plane.
  const padPxToWorld = useCallback(
    (px: number, py: number, planeZ: number): {x: number; y: number} => {
      const nx = (px / PAD_SIZE) * 2 - 1
      const ny = ((PAD_SIZE - py) / PAD_SIZE) * 2 - 1
      const ndc = new THREE.Vector3(nx, ny, 0.5)
      ndc.unproject(tempCamera)
      const cp = tempCamera.position
      const dx = ndc.x - cp.x
      const dy = ndc.y - cp.y
      const dz = ndc.z - cp.z
      // Ray parallel to plane (or hits behind camera) — fall back to camera XY.
      if (Math.abs(dz) < 1e-6) return {x: cp.x, y: cp.y}
      const t = (planeZ - cp.z) / dz
      if (!Number.isFinite(t)) return {x: cp.x, y: cp.y}
      return {x: cp.x + t * dx, y: cp.y + t * dy}
    },
    [tempCamera],
  )

  // World XY on plane Z = planeZ → pad pixel. Project the world point through
  // the camera to NDC, then map to pad coordinates.
  const worldToPadPx = useCallback(
    (wx: number, wy: number, planeZ: number): {px: number; py: number} => {
      const v = new THREE.Vector3(wx, wy, planeZ)
      v.project(tempCamera)
      const px = ((v.x + 1) / 2) * PAD_SIZE
      const py = PAD_SIZE - ((v.y + 1) / 2) * PAD_SIZE
      return {px, py}
    },
    [tempCamera],
  )

  const updateFromEvent = useCallback(
    (e: PointerEvent | ReactPointerEvent) => {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      // Clamp pointer to pad bounds before projecting so we never extrapolate
      // wildly past the screen edge.
      const clampedX = Math.max(0, Math.min(PAD_SIZE, mx))
      const clampedY = Math.max(0, Math.min(PAD_SIZE, my))
      const world = padPxToWorld(clampedX, clampedY, z)
      onChangeX(Math.round(Math.max(-MAX_TYPED_HALF_RANGE, Math.min(MAX_TYPED_HALF_RANGE, world.x))))
      onChangeY(Math.round(Math.max(-MAX_TYPED_HALF_RANGE, Math.min(MAX_TYPED_HALF_RANGE, world.y))))
    },
    [padPxToWorld, z, onChangeX, onChangeY],
  )

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      draggingRef.current = true
      updateFromEvent(e)
    },
    [updateFromEvent],
  )

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!draggingRef.current) return
      updateFromEvent(e)
    }
    const handlePointerUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false
        onDragEnd?.()
      }
    }
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [updateFromEvent, onDragEnd])

  // Draw the pad
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = PAD_SIZE * dpr
    canvas.height = PAD_SIZE * dpr
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, PAD_SIZE, PAD_SIZE)

    // Background
    ctx.fillStyle = tokens.sidebar.background
    ctx.beginPath()
    ctx.roundRect(0, 0, PAD_SIZE, PAD_SIZE, 6)
    ctx.fill()

    // Border
    ctx.strokeStyle = tokens.accordion.border
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.roundRect(0.5, 0.5, PAD_SIZE - 1, PAD_SIZE - 1, 6)
    ctx.stroke()

    // Crosshair anchored at the camera's center-ray hit on the Z=z plane. With
    // OrbitControls target = (0,0,0) this is where the world origin appears
    // on screen — useful as a visual reference for where (0,0,0) currently
    // sits on the pad.
    const origin = worldToPadPx(0, 0, z)
    const ox = Math.max(0, Math.min(PAD_SIZE, origin.px))
    const oy = Math.max(0, Math.min(PAD_SIZE, origin.py))
    ctx.strokeStyle = tokens.accordion.border
    ctx.lineWidth = 1
    ctx.setLineDash([3, 3])
    ctx.beginPath()
    ctx.moveTo(ox, 0)
    ctx.lineTo(ox, PAD_SIZE)
    ctx.moveTo(0, oy)
    ctx.lineTo(PAD_SIZE, oy)
    ctx.stroke()
    ctx.setLineDash([])

    // Axis labels
    ctx.fillStyle = tokens.text.muted
    ctx.font = '9px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('X', PAD_SIZE - 8, oy - 4)
    ctx.fillText('Y', ox + 8, 10)

    // Handle position — visually clamped to pad bounds so values outside the
    // current visible frame still render at the edge instead of disappearing.
    const raw = worldToPadPx(x, y, z)
    const hx = Math.max(HANDLE_RADIUS, Math.min(PAD_SIZE - HANDLE_RADIUS, raw.px))
    const hy = Math.max(HANDLE_RADIUS, Math.min(PAD_SIZE - HANDLE_RADIUS, raw.py))
    const isClamped = raw.px !== hx || raw.py !== hy

    // Handle glow
    ctx.beginPath()
    ctx.arc(hx, hy, HANDLE_RADIUS + 4, 0, Math.PI * 2)
    ctx.fillStyle = tokens.accent.glow
    ctx.fill()

    // Handle (dimmed when value sits outside the current visible frame)
    ctx.beginPath()
    ctx.arc(hx, hy, HANDLE_RADIUS, 0, Math.PI * 2)
    ctx.fillStyle = isClamped ? tokens.text.muted : tokens.accent.light
    ctx.fill()
  }, [x, y, z, tokens, worldToPadPx])

  const handleInputX = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) onChangeX(Math.max(-MAX_TYPED_HALF_RANGE, Math.min(MAX_TYPED_HALF_RANGE, val)))
    },
    [onChangeX],
  )

  const handleInputY = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) onChangeY(Math.max(-MAX_TYPED_HALF_RANGE, Math.min(MAX_TYPED_HALF_RANGE, val)))
    },
    [onChangeY],
  )

  const handleInputZ = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) onChangeZ(Math.max(Z_MIN, Math.min(Z_MAX, val)))
    },
    [onChangeZ],
  )

  const handleSliderZ = useCallback(
    (_: Event, value: number | number[]) => {
      onChangeZ(value as number)
    },
    [onChangeZ],
  )

  const sx = buildStyles(tokens)

  return (
    <Box sx={sx.wrapper}>
      {/* Left: pad + Z slider */}
      <Box sx={sx.padWithSlider}>
        <canvas
          ref={canvasRef}
          style={{width: PAD_SIZE, height: PAD_SIZE, cursor: 'pointer', touchAction: 'none'}}
          onPointerDown={handlePointerDown}
        />
        <Box sx={sx.verticalSliderBox}>
          <Typography sx={sx.verticalSliderLabel}>Z</Typography>
          <Slider
            value={z}
            min={Z_MIN}
            max={Z_MAX}
            step={1}
            orientation="vertical"
            onChange={handleSliderZ}
            onChangeCommitted={() => onDragEnd?.()}
            sx={sx.verticalSlider}
            size="small"
          />
          <Typography sx={sx.verticalSliderValue}>{z}</Typography>
        </Box>
      </Box>
      {/* Right: X / Y / Z inputs stacked vertically */}
      <Box sx={sx.inputs}>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            X
          </Box>
          <TextField
            value={x}
            onChange={handleInputX}
            onBlur={onDragEnd}
            size="small"
            type="number"
            sx={sx.numberInput}
          />
        </Box>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            Y
          </Box>
          <TextField
            value={y}
            onChange={handleInputY}
            onBlur={onDragEnd}
            size="small"
            type="number"
            sx={sx.numberInput}
          />
        </Box>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            Z
          </Box>
          <TextField
            value={z}
            onChange={handleInputZ}
            onBlur={onDragEnd}
            size="small"
            type="number"
            sx={sx.numberInput}
          />
        </Box>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  padWithSlider: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '8px',
  },
  verticalSliderBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '2px',
    minWidth: 24,
  },
  verticalSliderLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: tokens.text.muted,
  },
  verticalSlider: {
    flex: 1,
    '& .MuiSlider-thumb': {width: 10, height: 10},
    '& .MuiSlider-rail': {opacity: 0.3},
  },
  verticalSliderValue: {
    fontSize: 9,
    fontWeight: 500,
    color: tokens.text.secondary,
  },
  inputs: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  inputLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
    minWidth: 12,
  },
  numberInput: {
    width: 64,
    '& .MuiOutlinedInput-root': {
      fontSize: 12,
      color: tokens.text.primary,
    },
    '& .MuiOutlinedInput-input': {
      padding: '4px 6px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
  },
})

export default PositionPad3D
